import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

interface MoodStudyCorrelationProps {
  layoutMode?: 'normal' | 'compact';
}

interface MoodData {
  mood: string;
  emoji: string;
  label: string;
  totalHours: number;
  count: number;
  avgHours: number;
  color: string;
}

export default function MoodStudyCorrelation({ layoutMode = 'normal' }: MoodStudyCorrelationProps) {
  const { user } = useAuth();
  const [moodData, setMoodData] = useState<MoodData[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'week' | 'month' | 'all'>('month');

  const moods = [
    { value: 'great', emoji: '😊', label: '良い', color: '#10b981' },
    { value: 'normal', emoji: '😐', label: '普通', color: '#6b7280' },
    { value: 'down', emoji: '😔', label: '落ち込み', color: '#3b82f6' },
    { value: 'unmotivated', emoji: '😤', label: '無気力', color: '#f59e0b' },
    { value: 'motivated', emoji: '💪', label: 'やる気満々', color: '#ec4899' },
  ];

  const isCompact = layoutMode === 'compact';

  useEffect(() => {
    if (user) {
      fetchMoodData();
    }
  }, [user, period]);

  const fetchMoodData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const now = new Date();
      let startDate: Date | null = null;

      if (period === 'week') {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (period === 'month') {
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }

      let query = supabase
        .from('diary_entries')
        .select('mood, study_hours')
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .not('mood', 'is', null)
        .not('study_hours', 'is', null);

      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }

      const { data } = await query;

      if (!data) {
        setMoodData([]);
        setLoading(false);
        return;
      }

      // 気分ごとに集計
      const moodMap = new Map<string, { totalHours: number; count: number }>();
      
      data.forEach((entry) => {
        if (entry.mood && entry.study_hours !== null) {
          const current = moodMap.get(entry.mood) || { totalHours: 0, count: 0 };
          moodMap.set(entry.mood, {
            totalHours: current.totalHours + entry.study_hours,
            count: current.count + 1,
          });
        }
      });

      // データ整形
      const result: MoodData[] = moods.map((mood) => {
        const stats = moodMap.get(mood.value) || { totalHours: 0, count: 0 };
        return {
          mood: mood.value,
          emoji: mood.emoji,
          label: mood.label,
          totalHours: Math.round(stats.totalHours * 10) / 10,
          count: stats.count,
          avgHours: stats.count > 0 ? Math.round((stats.totalHours / stats.count) * 10) / 10 : 0,
          color: mood.color,
        };
      });

      setMoodData(result);
    } catch (error) {
      console.error('Error fetching mood data:', error);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{data.emoji}</span>
            <p className="font-semibold text-gray-800 dark:text-white">{data.label}</p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            平均: <span className="font-semibold text-teal-600 dark:text-teal-400">{data.avgHours}時間</span>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            合計: {data.totalHours}時間
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            記録数: {data.count}回
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const hasData = moodData.some(d => d.count > 0);

  return (
    <div className="space-y-4">
      {/* Period Filter */}
      <div className="flex items-center justify-between">
        <h4 className={`font-semibold text-gray-800 dark:text-white ${isCompact ? 'text-sm' : 'text-base'}`}>
          気分 × 勉強時間の相関
        </h4>
        <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setPeriod('week')}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              period === 'week'
                ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            1週間
          </button>
          <button
            onClick={() => setPeriod('month')}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              period === 'month'
                ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            1ヶ月
          </button>
          <button
            onClick={() => setPeriod('all')}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              period === 'all'
                ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            全期間
          </button>
        </div>
      </div>

      {!hasData ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full mx-auto mb-4">
            <i className="ri-emotion-line text-2xl text-gray-400 dark:text-gray-500"></i>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            まだ気分データがありません
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500">
            日記を書く際に気分を記録すると、ここに表示されます
          </p>
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className={isCompact ? 'h-48' : 'h-64'}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={moodData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
                <XAxis
                  dataKey="emoji"
                  tick={{ fontSize: isCompact ? 16 : 20 }}
                  stroke="#9ca3af"
                />
                <YAxis
                  tick={{ fontSize: isCompact ? 10 : 12, fill: '#9ca3af' }}
                  stroke="#9ca3af"
                  label={{
                    value: '平均時間',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: isCompact ? 10 : 12, fill: '#9ca3af' },
                  }}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(20, 184, 166, 0.1)' }} />
                <Bar dataKey="avgHours" radius={[8, 8, 0, 0]}>
                  {moodData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-5 gap-2">
            {moodData.map((mood) => (
              <div
                key={mood.mood}
                className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center border border-gray-200 dark:border-gray-700"
              >
                <div className="text-2xl mb-1">{mood.emoji}</div>
                <div className={`font-semibold text-gray-800 dark:text-white ${isCompact ? 'text-xs' : 'text-sm'}`}>
                  {mood.avgHours}h
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {mood.count}回
                </div>
              </div>
            ))}
          </div>

          {/* Insights */}
          {(() => {
            const maxMood = moodData.reduce((max, current) =>
              current.avgHours > max.avgHours ? current : max
            , moodData[0]);
            
            const minMood = moodData.filter(m => m.count > 0).reduce((min, current) =>
              current.avgHours < min.avgHours ? current : min
            , moodData.find(m => m.count > 0) || moodData[0]);

            if (maxMood.count > 0) {
              return (
                <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <i className="ri-lightbulb-line text-teal-600 dark:text-teal-400 text-lg mt-0.5"></i>
                    <div className="flex-1">
                      <p className={`text-teal-800 dark:text-teal-300 ${isCompact ? 'text-xs' : 'text-sm'}`}>
                        <span className="font-semibold">{maxMood.emoji} {maxMood.label}</span>の時が最も勉強時間が長く、
                        平均<span className="font-semibold">{maxMood.avgHours}時間</span>です。
                        {minMood.count > 0 && minMood.mood !== maxMood.mood && (
                          <>
                            <br />
                            <span className="font-semibold">{minMood.emoji} {minMood.label}</span>の時は
                            平均<span className="font-semibold">{minMood.avgHours}時間</span>と短めです。
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </>
      )}
    </div>
  );
}